import { z } from 'zod';

/**
 * Controlled vocabularies.
 *
 * Source: Product Taxonomy & Classification Model, Appendix A, plus the
 * Relationship Model and Canonical Entity Model. These are governed values —
 * new members require a taxonomy change request, not a code edit, once the
 * configuration store exists. Until then this file is the single definition
 * used by validation, templates, seed data and the UI.
 */

// ── Lifecycle and workflow ───────────────────────────────────────────────────

export const Lifecycle = z.enum([
  'Candidate',
  'Proposed',
  'Active',
  'Sunset',
  'Retired',
  'Superseded',
]);
export type Lifecycle = z.infer<typeof Lifecycle>;

export const DecisionStatus = z.enum([
  'Draft',
  'Submitted',
  'InReview',
  'ChangesRequested',
  'Approved',
  'Rejected',
  'Withdrawn',
]);
export type DecisionStatus = z.infer<typeof DecisionStatus>;

/** Mapping-set lifecycle. Published sets are immutable; change creates a version. */
export const MappingSetStatus = z.enum([
  'Draft',
  'Submitted',
  'ChangesRequested',
  'Approved',
  'Published',
  'Rejected',
  'Superseded',
]);
export type MappingSetStatus = z.infer<typeof MappingSetStatus>;

/** Relationship record status (Relationship Model §2). */
export const RelationshipStatus = z.enum([
  'Proposed',
  'InReview',
  'Approved',
  'Rejected',
  'Superseded',
]);
export type RelationshipStatus = z.infer<typeof RelationshipStatus>;

// ── Confidence, evidence and quality ─────────────────────────────────────────

export const ConfidenceBand = z.enum(['High', 'Medium', 'Low', 'Unknown']);
export type ConfidenceBand = z.infer<typeof ConfidenceBand>;

/**
 * How a confidence value was arrived at. Kept separate from the band itself
 * because "approved by governance" and "matched on a stable identifier" are
 * different kinds of trust — see the Relationship Model's warning that
 * approval does not make poor source data accurate.
 */
export const ConfidenceMethod = z.enum([
  'SystemDerivedExact',
  'RuleDerivedDeterministic',
  'AssistedCandidate',
  'UserAsserted',
  'SpecialistReviewed',
  'GovernanceApproved',
]);
export type ConfidenceMethod = z.infer<typeof ConfidenceMethod>;

export const EvidenceStatus = z.enum(['Available', 'Restricted', 'Unavailable', 'Superseded']);
export type EvidenceStatus = z.infer<typeof EvidenceStatus>;

export const QualityStatus = z.enum(['Unverified', 'Validated', 'Certified', 'Disputed', 'Stale']);
export type QualityStatus = z.infer<typeof QualityStatus>;

// ── Health, disposition and allocation ───────────────────────────────────────

export const ProductHealth = z.enum(['Healthy', 'AtRisk', 'HighRisk', 'Critical', 'Unknown']);
export type ProductHealth = z.infer<typeof ProductHealth>;

export const Disposition = z.enum([
  'Retain',
  'Modernize',
  'Consolidate',
  'Replace',
  'Retire',
  'Investigate',
]);
export type Disposition = z.infer<typeof Disposition>;

export const RelationshipRole = z.enum([
  'Primary',
  'Supporting',
  'Shared',
  'Dependency',
  'Candidate',
]);
export type RelationshipRole = z.infer<typeof RelationshipRole>;

/**
 * Allocation status. `Unallocated` is a first-class outcome, not an error:
 * the specification is emphatic that a missing allocation must be shown rather
 * than silently apportioned.
 */
export const AllocationStatus = z.enum([
  'Allocated',
  'PartiallyAllocated',
  'Unallocated',
  'Disputed',
]);
export type AllocationStatus = z.infer<typeof AllocationStatus>;

export const AllocationBasis = z.enum(['Direct', 'Usage', 'Headcount', 'Equal', 'Manual', 'Other']);
export type AllocationBasis = z.infer<typeof AllocationBasis>;

// ── Classification ───────────────────────────────────────────────────────────

/**
 * The nine operating constructs. C03 Data Product and C09 Undetermined exist in
 * the Taxonomy model but not in the Vision deck's seven-class slide; the
 * Taxonomy model wins (recorded as an open item in PVI_Understanding.md §16 A7).
 */
export const ClassificationClass = z.enum([
  'BusinessProduct', // C01
  'PlatformProduct', // C02
  'DataProduct', // C03
  'SharedService', // C04
  'ApplicationComponent', // C05
  'UtilityCommodity', // C06
  'ProjectInitiative', // C07
  'RetireConsolidate', // C08
  'Undetermined', // C09
]);
export type ClassificationClass = z.infer<typeof ClassificationClass>;

export const CLASSIFICATION_CLASS_CODES: Record<ClassificationClass, string> = {
  BusinessProduct: 'C01',
  PlatformProduct: 'C02',
  DataProduct: 'C03',
  SharedService: 'C04',
  ApplicationComponent: 'C05',
  UtilityCommodity: 'C06',
  ProjectInitiative: 'C07',
  RetireConsolidate: 'C08',
  Undetermined: 'C09',
};

export const CLASSIFICATION_CLASS_LABELS: Record<ClassificationClass, string> = {
  BusinessProduct: 'Business Product',
  PlatformProduct: 'Platform Product',
  DataProduct: 'Data Product',
  SharedService: 'Shared Service',
  ApplicationComponent: 'Application / Component',
  UtilityCommodity: 'Utility / Commodity',
  ProjectInitiative: 'Project / Initiative',
  RetireConsolidate: 'Retire / Consolidate',
  Undetermined: 'Undetermined / Discovery Required',
};

/** Boundary anti-patterns the engine must surface (Taxonomy §6). */
export const AntiPattern = z.enum([
  'ApplicationEqualsProduct',
  'ProductTooBroad',
  'ProductTooNarrow',
  'PlatformMasqueradingAsBusinessProduct',
  'SharedServiceMasqueradingAsProduct',
  'ProjectMasqueradingAsProduct',
  'DuplicateProducts',
  'OrphanApplication',
]);
export type AntiPattern = z.infer<typeof AntiPattern>;

// ── Security and classification ──────────────────────────────────────────────

/**
 * The Canonical Entity Model names three classes; the Persona pack's access
 * model adds `Public`. We carry all four so the stricter of the two documents
 * can be satisfied without a schema change (PVI_Understanding.md §16 A8).
 */
export const DataClassification = z.enum(['Public', 'Internal', 'Confidential', 'Restricted']);
export type DataClassification = z.infer<typeof DataClassification>;

export const FunctionalPermission = z.enum([
  'view',
  'create',
  'edit',
  'propose',
  'approve',
  'administer',
  'export',
]);
export type FunctionalPermission = z.infer<typeof FunctionalPermission>;

// ── Process and organisation ─────────────────────────────────────────────────

export const ProcessLevel = z.enum(['L1', 'L2', 'L3', 'L4']);
export type ProcessLevel = z.infer<typeof ProcessLevel>;

export const Criticality = z.enum(['MissionCritical', 'BusinessCritical', 'Important', 'Low']);
export type Criticality = z.infer<typeof Criticality>;

export const TechnicalHealth = z.enum(['Good', 'Degraded', 'AtRisk', 'Poor', 'Unknown']);
export type TechnicalHealth = z.infer<typeof TechnicalHealth>;

export const OrganizationalUnitType = z.enum([
  'Division',
  'Department',
  'BusinessDomain',
  'Function',
]);
export type OrganizationalUnitType = z.infer<typeof OrganizationalUnitType>;

// ── Ingestion ────────────────────────────────────────────────────────────────

export const IngestionRunStatus = z.enum([
  'Queued',
  'Running',
  'Completed',
  'Warning',
  'Failed',
  'Cancelled',
]);
export type IngestionRunStatus = z.infer<typeof IngestionRunStatus>;

export const MappingRuleLifecycle = z.enum([
  'Draft',
  'Validated',
  'BusinessReviewed',
  'Approved',
  'Published',
  'Superseded',
  'Retired',
]);
export type MappingRuleLifecycle = z.infer<typeof MappingRuleLifecycle>;

/** Transformation types TR-001…TR-010 from the Source-to-Canonical catalog. */
export const TransformationType = z.enum([
  'Direct',
  'Lookup',
  'Parse',
  'Compose',
  'Split',
  'Default',
  'Derive',
  'Match',
  'Relationship',
  'EvidenceLink',
]);
export type TransformationType = z.infer<typeof TransformationType>;

/** Validation rule types VAL-001…VAL-015. */
export const ValidationRuleType = z.enum([
  'RequiredField',
  'TypeParsing',
  'ControlledVocabulary',
  'Uniqueness',
  'ReferentialIntegrity',
  'HierarchyConsistency',
  'EffectiveDates',
  'RelationshipEndpoints',
  'Allocation',
  'Authorization',
  'Sensitivity',
  'VersionCompatibility',
  'Concurrency',
  'Lineage',
  'Evidence',
]);
export type ValidationRuleType = z.infer<typeof ValidationRuleType>;

export const IssueSeverity = z.enum(['Error', 'Warning', 'Info']);
export type IssueSeverity = z.infer<typeof IssueSeverity>;
